import { useEffect } from 'react';

interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

interface PageView {
  page_title: string;
  page_location: string;
}

class Analytics {
  private isEnabled: boolean = false;

  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'production' && typeof window !== 'undefined';
  }

  // Initialize analytics
  init() {
    if (!this.isEnabled) return;

    // Google Analytics 4
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: document.title,
        page_location: window.location.href,
      });
    }

    // Track page view
    this.trackPageView({
      page_title: document.title,
      page_location: window.location.href,
    });
  }

  // Track page views
  trackPageView(data: PageView) {
    if (!this.isEnabled) return;

    console.log('📊 Page View:', data);

    // Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_title: data.page_title,
        page_location: data.page_location,
      });
    }
  }

  // Track custom events
  trackEvent(event: AnalyticsEvent) {
    if (!this.isEnabled) return;

    console.log('📊 Event:', event);

    // Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
    }
  }

  // Track user interactions
  trackInteraction(interaction: string, element?: string) {
    this.trackEvent({
      action: 'interaction',
      category: 'user_engagement',
      label: `${interaction}${element ? `_${element}` : ''}`,
    });
  }

  // Track form submissions
  trackFormSubmission(formName: string, success: boolean = true) {
    this.trackEvent({
      action: 'form_submit',
      category: 'engagement',
      label: `${formName}_${success ? 'success' : 'error'}`,
    });
  }

  // Track project views
  trackProjectView(projectName: string) {
    this.trackEvent({
      action: 'view_project',
      category: 'portfolio',
      label: projectName,
    });
  }

  // Track social media clicks
  trackSocialClick(platform: string) {
    this.trackEvent({
      action: 'social_click',
      category: 'engagement',
      label: platform,
    });
  }

  // Track download/action buttons
  trackDownload(fileName: string) {
    this.trackEvent({
      action: 'download',
      category: 'engagement',
      label: fileName,
    });
  }
}

// Create analytics instance
const analytics = new Analytics();

// Custom hook for analytics
export const useAnalytics = () => {
  useEffect(() => {
    analytics.init();
  }, []);

  return {
    trackPageView: analytics.trackPageView.bind(analytics),
    trackEvent: analytics.trackEvent.bind(analytics),
    trackInteraction: analytics.trackInteraction.bind(analytics),
    trackFormSubmission: analytics.trackFormSubmission.bind(analytics),
    trackProjectView: analytics.trackProjectView.bind(analytics),
    trackSocialClick: analytics.trackSocialClick.bind(analytics),
    trackDownload: analytics.trackDownload.bind(analytics),
  };
};

export default analytics;
